import { Button, DialogOverlay, DialogOverlayActions, DialogOverlayFooter, Paragraph } from '@fs/zion-ui'
import React from 'react'
import { useTranslation } from 'react-i18next'
import useNotifications from '../../../hooks/useNotifications'
import { updateGroupMember } from '../../../services/groupsService'
import DemotePromoteMembersList from './DemotePromoteMembersList'
import { getIdElement } from '../../../util/StringUtil'

export default function DemotePromoteMembersDialogOverlay({ members, refreshData, ...overlay }) {
  const { t } = useTranslation()
  const { error, ok } = useNotifications()
  const membersToDemote = getMembersToDemotePromote(members, 'ADMIN')
  const membersToPromote = getMembersToDemotePromote(members, 'MEMBER')
  const headingText = getHeadingText(members, membersToDemote, membersToPromote)
  const bodyText = getBodyText(members, membersToDemote, membersToPromote)

  const confirmBtnOnClick = async () => {
    overlay.close()
    const updateGroupMemberPromises = members.map((member) => {
      const groupMemberUpdateProperties = {
        status: member.status === 'MEMBER' ? 'ADMIN' : 'MEMBER',
        notifyOnJoin: member.notifyOnJoin,
      }
      return updateGroupMember(member.groupId, member.memberCisId, groupMemberUpdateProperties)
    })
    const updateGroupMemberResults = await Promise.allSettled(updateGroupMemberPromises)

    const rejectedPromises = updateGroupMemberResults.filter((result) => result.status === 'rejected')
    if (rejectedPromises.length > 0) {
      let failedToPromote = ''
      let failedToDemote = ''
      rejectedPromises.forEach((result) => {
        const payload = JSON.parse(result.reason.config.data)
        const status = payload.status
        const cisId = getIdElement(result.reason.config.url)
        if (status === 'ADMIN') {
          failedToPromote += `${cisId}, `
        } else {
          failedToDemote += `${cisId}, `
        }
      })
      if (failedToPromote) {
        error(
          t('groups.errors.promotingMembers', 'Error promoting {users}', {
            users: failedToPromote.slice(0, -2),
          })
        )
      }
      if (failedToDemote) {
        error(
          t('groups.errors.demotingMembers', 'Error demoting {users}', {
            users: failedToDemote.slice(0, -2),
          })
        )
      }
    } else {
      ok(t('success', 'Success'))
    }
    if (rejectedPromises.length < updateGroupMemberResults.length) {
      refreshData()
    }
  }

  return (
    <DialogOverlay id="Demote/Promote DialogOverlay" {...overlay} title={t(headingText.key, headingText.value)}>
      <Paragraph size="sm">{t(bodyText.key, bodyText.value)}</Paragraph>
      <DemotePromoteMembersList membersToDemote={membersToDemote} membersToPromote={membersToPromote} />
      <DialogOverlayFooter>
        <DialogOverlayActions>
          <Button emphasis="high" onClick={confirmBtnOnClick}>
            {t('action.confirm', 'Confirm')}
          </Button>
          <Button emphasis="medium" onClick={overlay.close}>
            {t('action.cancel', 'Cancel')}
          </Button>
        </DialogOverlayActions>
      </DialogOverlayFooter>
    </DialogOverlay>
  )
}

function getMembersToDemotePromote(members, memberType) {
  return members.filter((member) => member.status === memberType)
}

function getHeadingText(members, membersToDemote, membersToPromote) {
  const headingText = {
    key: 'group.members.dialogOverlay.demotePromote.heading',
    value: 'Demote & Promote Members',
  }
  if (members.length === membersToDemote.length) {
    headingText.key = 'group.members.dialogOverlay.demote.heading'
    headingText.value = 'Demote Members'
  } else if (members.length === membersToPromote.length) {
    headingText.key = 'group.members.dialogOverlay.promote.heading'
    headingText.value = 'Promote Members'
  }
  return headingText
}

function getBodyText(members, membersToDemote, membersToPromote) {
  const bodyText = {
    key: 'group.members.dialogOverlay.demotePromote.body',
    value: 'Are you sure you want to demote and promote these members?',
  }
  if (members.length === membersToDemote.length) {
    if (members.length === 1) {
      bodyText.key = 'group.members.dialogOverlay.demote.body.singleMember'
      bodyText.value = 'Are you sure you want to demote this member from Admin to Member?'
    } else {
      bodyText.key = 'group.members.dialogOverlay.demote.body.multipleMembers'
      bodyText.value = 'Are you sure you want to demote these members from Admins to Members?'
    }
  } else if (members.length === membersToPromote.length) {
    if (members.length === 1) {
      bodyText.key = 'group.members.dialogOverlay.promote.body.singleMember'
      bodyText.value = 'Are you sure you want to promote this member from Member to Admin?'
    } else {
      bodyText.key = 'group.members.dialogOverlay.promote.body.multipleMembers'
      bodyText.value = 'Are you sure you want to promote these members from Members to Admins?'
    }
  }
  return bodyText
}
